import { Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { ButtonModule } from '@ui/form/button/button.module';
import { HeadlineModule } from '@ui/layout/headline/headline.module';
import { HeadlineWrapperModule } from '@ui/layout/headline-wrapper/headline-wrapper.module';
import { TranslatePipe } from '@ngx-translate/core';
import { OnlyViewMode } from '@core/privileges/only-view-mode';
import { PrivilegesModules } from '@core/privileges/privileges-modules';
import { filter, Observable } from 'rxjs';
import { ContentWrapperModule } from '@ui/layout/content-wrapper/content-wrapper.module';
import { PartnerFormComponent } from './partner-form/partner-form.component';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingQueryParamsName } from '@core/router/routing.params';
import { PartnersState } from '../state/partners.state';
import { B2BPartnerAddRequest } from '@api/models/B2bService/Contract/B2bPartner/b-2-b-partner-add-request';
import { AbstractFormHandlers } from '@ui/base/abstract-form/abstract-form.component';
import { getLoadingProp } from '@core/state/operators/start-with-loading';
import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { RxEffects } from '@rx-angular/state/effects';
import { PushService } from '@core/push/push.service';
import { PushCodeModel } from '@core/push/push-code.model';
import { RouteEntity } from '@core/router/route.entity';
import { B2BVisOrgListResponse } from '@api/models/B2bService/Contract/B2bPartner/b-2-b-vis-org-list-response';
import { map } from 'rxjs/operators';
import { PreLoaderModule } from '@ui/feedback/pre-loader/pre-loader.module';
import { B2BPartnerSetRequest } from '@api/models/B2bService/Contract/B2bPartner/b-2-b-partner-set-request';
import { parseNumber } from '@libs/functions';
import { isNumber } from 'lodash-es';


@Component({
    selector: 'users-partner',
    imports: [
        AsyncPipe,
        ButtonModule,
        HeadlineModule,
        HeadlineWrapperModule,
        ContentWrapperModule,
        PartnerFormComponent,
        PreLoaderModule,
        TranslatePipe,
    ],
    templateUrl: './partner.component.html',
    styleUrl: './partner.component.scss',
    providers: [RxEffects, PartnersState],
})
export class PartnerComponent {
    @OnlyViewMode([PrivilegesModules.AcmanUsers])
    public readonly readonlyMode$: Observable<boolean>;

    readonly #route: ActivatedRoute = inject(ActivatedRoute);
    readonly #router: Router = inject(Router);
    readonly #partnersState: PartnersState = inject(PartnersState);
    readonly #effects: RxEffects = inject(RxEffects);
    readonly #push: PushService = inject(PushService);

    public readonly partnerId: number = parseNumber(this.#route.snapshot.paramMap.get(RoutingQueryParamsName.OrgId));
    public readonly isEditFormMode: boolean = isNumber(this.partnerId);

    public readonly organizations$: Observable<B2BVisOrgListResponse[]> = this.#partnersState.organizations$.pipe(
        map((orgs: B2BVisOrgListResponse[]) => orgs?.filter((org: B2BVisOrgListResponse) => !org.isPrtnr)),
    );

    public readonly pending$: Observable<boolean> = this.#partnersState.loading$.pipe(getLoadingProp(StartWithUnit.AddOrEditPartner));

    public readonly partner$: Observable<B2BVisOrgListResponse> = this.#partnersState.organizations$.pipe(
        map((orgs: B2BVisOrgListResponse[]) => orgs?.find((org: B2BVisOrgListResponse) => org.orgId === this.partnerId)),
        filter(Boolean),
    );


    public readonly handlersForm: AbstractFormHandlers = {
        handlerCreate: (body: B2BPartnerAddRequest) =>
            this.#effects.register(this.#partnersState.addPartner(body), () => {
                this.#push.success(PushCodeModel.SuccessAdd);
                this.#router.navigate([RouteEntity.Root, RouteEntity.Partners]);
            }),
        handlerSave: (body: B2BPartnerSetRequest) =>
            this.#effects.register(this.#partnersState.editPartner(body), () => {
                this.#push.success(PushCodeModel.SuccessChange);
                this.#router.navigate([RouteEntity.Root, RouteEntity.Partners]);
            }),
    };

    protected readonly routeEntity: typeof RouteEntity = RouteEntity;
}
